// FHIR
import Client from "fhir-kit-client";
import { Parameters, Bundle, OperationOutcome } from "fhir/r5";

/////////////////////////////////////
//             Client              //
/////////////////////////////////////

const fhirOperationClient = new Client({
  baseUrl: process.env.REACT_APP_VALIDATE_URL ?? "fhir",
});

//////////////////////////////////////
//             Functions            //
//////////////////////////////////////

/**
 * Returns the validator ID based on the output type.
 * IDs are configured via environment variables.
 *
 * @param type "JSON", "CSV", "XML", "HL7V2", "JSONFHIR"
 * @returns string | undefined
 */
export function getValidatorIdForType(type: string): string | undefined {
  switch (type) {
    case "JSON":
      return process.env.REACT_APP_VALIDATOR_JSON_ID;
    case "CSV":
      return process.env.REACT_APP_VALIDATOR_CSV_ID;
    case "XML":
      return process.env.REACT_APP_VALIDATOR_XML_ID;
    case "HL7v2":
      return process.env.REACT_APP_VALIDATOR_HL7V2_ID;
    default:
      return process.env.REACT_APP_VALIDATOR_FHIR_JSON_ID;
  }
}

/**
 * Validates a resource via the FHIR $validate operation.
 *
 * @param input The input to validate (must contain type, content, profile, etc.)
 * @param validatorId The ID of the validator to use
 * @returns The FHIR response (Bundle)
 */
export async function validateResource(input: any, validatorId?: string): Promise<Bundle> {
  let parameter: Parameters = {
    resourceType: "Parameters",
    parameter: [
      {
        name: "validatorId",
        valueString: validatorId ?? getValidatorIdForType(input.type ?? "JSON"),
      },
      {
        name: "resource",
        valueString: input.content,
      },
    ],
  };
  if (input.profile) {
    parameter.parameter?.push({
      name: "profile",
      valueCanonical: input.profile,
    });
  }
  return await fhirOperationClient.operation({
    name: "validate",
    input: parameter,
  });
}

/**
 * Parses the FHIR $validate response and updates the input with validation report ID and status.
 *
 * @param response the FHIR response (Bundle)
 * @param input
 */
export function parseValidateResponse(response: Bundle, input: any) {
  let validationStatus: any = "";
  input.validationReportID = response.id;
  let responseFiltered = response.entry?.filter((l) => l.resource?.resourceType === "OperationOutcome")[0]
    ?.resource as OperationOutcome;
  if (
    responseFiltered &&
    responseFiltered.issue &&
    responseFiltered.issue[0] &&
    responseFiltered.issue[0].details &&
    responseFiltered.issue[0].details.coding
  ) {
    validationStatus = responseFiltered.issue[0].details.coding[0].code;
    input.validationReportStatus = validationStatus ? validationStatus : "";
  }
}
